import React, { useState, useMemo, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import classnames from 'classnames'
import { Content, List, Flex, Button, TextInput,
  Popup, Dialog, Pane, Text, Toast } from '@leverege/ui-elements'

import Editor from '../../editors/enums'
import { ListItem } from '~/views'

import S from './EnumStyles.css'

function EnumValues( props ) {
  const { enumType, actions, dispatch, values = [] } = props
  const [ showCreate, setShowCreate ] = useState( false )
  const [ active, setActive ] = useState()
  const [ toDelete, setToDelete ] = useState()

  const closeCreateDialog = () => setShowCreate( false )
  const openCreateDialog = () => setShowCreate( true )
  const onSelect = ( e, item ) => setActive( item )
  const onCreate = async ( { value, name } ) => {
    try {
      await dispatch( actions( enumType ).addValue( value, { name } ) )
      Toast.success( `Value ${value} created`, {} )
    } catch ( ex ) {
      Toast.error( [ `Unable to create ${value}`, ex.message ] )
      console.error( ex )
    }
    closeCreateDialog()
  }

  const onDelete = async () => {
    await dispatch( actions( enumType ).removeValue( toDelete.data ) )
    Toast.success( `Value ${toDelete.data} removed`, {} )
    setToDelete()
  }

  const ValueFactory = {
    create : useMemo( () => function( item ) {
      const { value, name } = item
      const isActive = active?.value && active.value === value
      return (
        <ListItem
          onEvent={onSelect}
          item={item}
          selected={isActive}
          title={name}
          subtitle={value}
          rightView={(
            <Flex justify="flex-end">
              <Popup title={(
                <>
                  <i className="fa fa-gear fa-fw"/>
                  <i style={{ fontSize : 12 }} className="fa-chevron-down fa fa-fw"/>
                </>
              )}>
                <Popup.Item eventData={value} onClick={setToDelete}>Delete</Popup.Item>
              </Popup>
            </Flex>
          )} />
      )
    }, [ active ] )
  }

  const editor = Editor.createEditor( {
    enumType,
    type : 'value',
    actions,
    item : values.find( v => v.value === active?.value ) } )

  return (
    <Content>
      <Content.Header>
        <Flex justify="space-between" className={S.header}>
          <Text variant="screenTitle">{`${enumType}'s Values`}</Text>
          <Button
            variant="add"
            icon="fa fa-fw fa-circle-plus fa-lg"
            onClick={openCreateDialog}>
            New Value
          </Button>
        </Flex>
        <NotFound values={values} />
      </Content.Header>
      <Content.Area >
        <Pane className={classnames( S.screen, { [S.split] : !!active } )}>
          <Pane>
            <List.Dynamic items={values} factory={ValueFactory} />
          </Pane>
          {active && editor}
        </Pane>
      </Content.Area>
      <CreateForm onCreate={onCreate} onCancel={closeCreateDialog} show={showCreate} />
      <Dialog.Question
        variant="dialogMedium"
        show={toDelete?.data}
        title={`Delete ${toDelete?.data}?`} // eslint-disable-line max-len
        onOkay={onDelete}
        onCancel={setToDelete}
        okay={`Delete ${toDelete?.data}`}
        okayVariant="primaryDestructive" />
    </Content>
  )
}

function CreateForm( { onCancel, onCreate, show } ) {
  const [ name, setName ] = useState()
  const [ value, setValue ] = useState()
  const submitButton = useRef()
  const onSubmit = () => onCreate( { value : value.value, name : name?.value || value?.value } )

  useEffect( () => {
    const submitOnReturn = ( e ) => {
      if ( ( e.which === 13 || e.keyCode === 13 ) && submitButton.current ) {
        submitButton.current.onClick( e )
      }
    }
    if ( show ) {
      document.addEventListener( 'keyup', submitOnReturn )
    } else {
      document.removeEventListener( 'keyup', submitOnReturn )
    }
    return () => document.removeEventListener( 'keyup', submitOnReturn )
  }, [ show ] )

  return (
    <Dialog
      variant="dialogMedium"
      show={show}
      onClose={onCancel}
      title="Create New Value"
      footer={(
        <Flex variant="dialogButtons">
          <Button onClick={onCancel} variant="secondary">Cancel</Button>
          <Button onClick={onSubmit} disabled={!value} ref={submitButton}> Create </Button>
        </Flex>
      )}>
      <Flex direction="column" align="flex-start">
        <TextInput className={S.input} changeOnReturn={false} onChange={setValue} hint="Value"/>
        <TextInput className={S.input} changeOnReturn={false} onChange={setName} hint="Name"/>
      </Flex>
    </Dialog>
  )
}

function NotFound( { values } ) {
  return values?.length ? null : (
    <Flex justify="center" style={{ width : '100%' }}><Text>No values yet. Click + to add values.</Text></Flex>
  )
}

export default connect( )( EnumValues )
